import { Form, message } from 'antd';
import type { FormInstance } from 'antd';
import type React from 'react';
import { useCallback, useState } from 'react';

import type { PortfolioProjectRow } from '@/api/portfolio/api';
import type {
  CreateProjectFromSessionDTO,
  ResolveSharePointSharingLinkResponseDTO,
  UpdateProjectPayload,
} from '@/api/projects/api';
import { getClientProject, updateProject } from '@/api/projects/api';
import { useAuthContext } from '@/hooks/auth.store';

export interface EditProjectFormValues {
  sharepoint_site_id?: string;
  sharepoint_drive_id?: string;
  sharepoint_folder_item_id?: string;
  sharepoint_folder_web_url?: string;
}

export interface UseProjectSharePointEditReturn {
  isEditModalOpen: boolean;
  editingRow: PortfolioProjectRow | null;
  isEditLoading: boolean;
  isEditSubmitting: boolean;
  editProjectForm: FormInstance<EditProjectFormValues>;
  messageContext: React.ReactElement;
  openEditProject: (row: PortfolioProjectRow) => void;
  closeEditModal: () => void;
  handleSharePointResolved: (resolved: ResolveSharePointSharingLinkResponseDTO | null) => void;
  handleEditProjectSubmit: () => Promise<void>;
}

export function useProjectSharePointEdit(): UseProjectSharePointEditReturn {
  const { accessToken } = useAuthContext();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<PortfolioProjectRow | null>(null);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [editProjectForm] = Form.useForm<EditProjectFormValues>();
  const [messageApi, messageContext] = message.useMessage();

  const openEditProject = useCallback(
    async (row: PortfolioProjectRow) => {
      setEditingRow(row);
      setIsEditModalOpen(true);
      editProjectForm.resetFields();
      setIsEditLoading(true);
      try {
        const project: CreateProjectFromSessionDTO = await getClientProject(
          accessToken!,
          row.client_id,
          row.id,
        );
        editProjectForm.setFieldsValue({
          sharepoint_site_id: project.sharepoint_site_id ?? '',
          sharepoint_drive_id: project.sharepoint_drive_id ?? '',
          sharepoint_folder_item_id: project.sharepoint_folder_item_id ?? '',
          sharepoint_folder_web_url: project.sharepoint_folder_web_url ?? '',
        });
      } catch {
        void messageApi.warning('Could not load project data');
      } finally {
        setIsEditLoading(false);
      }
    },
    [accessToken, editProjectForm, messageApi],
  );

  const closeEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    setEditingRow(null);
    editProjectForm.resetFields();
  }, [editProjectForm]);

  const handleSharePointResolved = useCallback(
    (resolved: ResolveSharePointSharingLinkResponseDTO | null) => {
      editProjectForm.setFieldsValue({
        sharepoint_site_id: resolved?.sharepoint_site_id ?? '',
        sharepoint_drive_id: resolved?.sharepoint_drive_id ?? '',
        sharepoint_folder_item_id: resolved?.sharepoint_folder_item_id ?? '',
        sharepoint_folder_web_url: resolved?.sharepoint_folder_web_url ?? '',
      });
    },
    [editProjectForm],
  );

  const handleEditProjectSubmit = useCallback(async () => {
    if (!editingRow) {
      void messageApi.error('Missing project context');
      return;
    }
    const values = await editProjectForm.validateFields();

    const s1 = (values.sharepoint_site_id ?? '').trim();
    const s2 = (values.sharepoint_drive_id ?? '').trim();
    const s3 = (values.sharepoint_folder_item_id ?? '').trim();
    const s4 = (values.sharepoint_folder_web_url ?? '').trim();

    const allSet = s1 && s2 && s3 && s4;
    const allEmpty = !s1 && !s2 && !s3 && !s4;
    if (!allSet && !allEmpty) {
      void messageApi.error(
        'SharePoint binding must be all fields set or all empty. Use Resolve or clear.',
      );
      return;
    }

    const payload: UpdateProjectPayload = {
      sharepoint_site_id: s1 || null,
      sharepoint_drive_id: s2 || null,
      sharepoint_folder_item_id: s3 || null,
      sharepoint_folder_web_url: s4 || null,
    };

    setIsEditSubmitting(true);
    try {
      await updateProject(accessToken!, editingRow.client_id, editingRow.id, payload);
      void messageApi.success('SharePoint folder saved');
      closeEditModal();
    } catch (error_) {
      void messageApi.error(error_ instanceof Error ? error_.message : 'Failed to save');
    } finally {
      setIsEditSubmitting(false);
    }
  }, [accessToken, closeEditModal, editingRow, editProjectForm, messageApi]);

  return {
    isEditModalOpen,
    editingRow,
    isEditLoading,
    isEditSubmitting,
    editProjectForm,
    messageContext,
    openEditProject,
    closeEditModal,
    handleSharePointResolved,
    handleEditProjectSubmit,
  };
}
